import React, { useEffect, useState, useRef, Fragment } from "react";
import {
  storage,
  db,
  auth,
  deleteObject,
  ref,
  uploadString,
  uploadBytes,
  getStorage,
  listAll,
} from "../../base";
import { Form, Modal, Button, Collapse } from "react-bootstrap";
import "../account/account.css";
import {
  addDoc,
  collection,
  onSnapshot,
  query,
  where,
  setDoc,
  doc,
  deleteDoc,
  updateDoc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import "../loader.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Edit,
  CloudDownload,
  Delete,
  Build,
  OpenInFull,
} from "@mui/icons-material";
import { Tooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import { useProjectLimits } from '../../hooks/useProjectLimits';
import featureFlags from '../../config/featureFlags';


function Account(props) {
  const [loading, setLoading] = useState(true);
  const [localProjects, setLocalProjects] = useState([]);
  // const [projects, setProjects] = useState([]);
  const [lastProjectId, setLastProjectId] = useState("");
  const [projectUrl, setProjectUrl] = useState("");
  const [projectName, setProjectName] = useState("");
  const [closeUpload, setCloseUpload] = useState(false);
  const [closeAccount, setCloseAccount] = useState(true);
  const [imageURL, setImageURL] = useState("");
  const [imageName, setImageName] = useState("");
  const [canvasJson, setCanvasJson] = useState({});
  const [isProjectList, setIsProjectList] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const [userName, setUserName] = useState("");
  const [userPlan, setUserPlan] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [userData, setUserData] = useState(null);

  const projectImageRef = useRef();

  const { t, i18n } = useTranslation(["translation"]);
  const currentUser = props.currentUser || auth.currentUser;
  const loggedInUserId = currentUser?.uid || "";

  // Get project limits data
  const { projectCount, projectLimit, isLoading: limitsLoading, canCreateProject, isAtLimit } = useProjectLimits(currentUser, userData);

  const projectNameRef = useRef();
  const isMountedRef = useRef(true);
  const isGuestUser = currentUser && currentUser.email.startsWith("guest_");
  const tooltipMessage = t('tooltip-message', {
    defaultValue: "This feature is only available for registered users.",
  });
  const trialDeleteLockMessage = t('account.deleteDisabledRetainsSlot', {
    defaultValue: 'Upgrade to Standard or Pro to unlock project deletion. Free trial and My Project slots are single-use.'
  });
  const activePlanName = userData?.planName || userData?.planType || userData?.currentPlan || '';
  const hasActiveStandardTrial = Boolean(
    userData?.standardTrialStatus === 'active' &&
    userData?.standardTrialEndsAt &&
    (() => {
      const rawEndsAt = userData.standardTrialEndsAt;
      const endsAt = typeof rawEndsAt?.toDate === 'function' ? rawEndsAt.toDate() : new Date(rawEndsAt);
      return endsAt.getTime() > Date.now();
    })()
  );
  const hasDeletionUnlockAccess = Boolean(
    ['standard_plan', 'pro_plan', 'Standard', 'Pro'].includes(activePlanName) ||
    (userData?.isSubscription && ['active', 'cancel_at_period_end'].includes(userData?.subscriptionStatus))
  );
  const isMyProjectPlan = ['my_project_plan', 'My Project'].includes(activePlanName);
  const isDeleteLocked = Boolean(isMyProjectPlan || (hasActiveStandardTrial && !hasDeletionUnlockAccess));

  const isProjectLoaded = () => {
    return (
      props.canvas &&
      (props.canvas.getObjects().length > 0 || props.canvas.backgroundImage)
    );
  };

  const projectSource = localProjects.length > 0 ? localProjects : (props.projects || []);

  const filteredProjects = projectSource
    .filter((project) => {
      const normalizedProjectName = String(project?.projectName || "").toLowerCase();
      return normalizedProjectName.includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      // Sort by last saved date (newest first)
      // Check for lastSaved, savedTimestamp, or createdAt fields
      const getProjectDate = (project) => {
        if (project.lastSaved) return new Date(project.lastSaved);
        if (project.savedTimestamp) return new Date(project.savedTimestamp);
        if (project.createdAt) return new Date(project.createdAt);
        if (project.timestamp) return new Date(project.timestamp);
        return new Date(0); // Fallback to epoch if no date found
      };

      return getProjectDate(b) - getProjectDate(a); // Newest first
    });

  const hasNoProjectSlots = Number(projectLimit) === 0;
  const getProjectDisplayName = (project) => {
    const rawName = String(project?.projectName || "").trim();
    return rawName ? rawName.replaceAll("_", " ") : t("account.untitledProject", { defaultValue: "Untitled project" });
  };

  const getLocalizedPricingPath = () => {
    const normalizedLanguage = i18n.language || 'en';

    if (normalizedLanguage === 'fr-CA') return '/app/fr-CA#pricing';
    if (normalizedLanguage === 'fr') return '/app/fr#pricing';
    if (normalizedLanguage === 'en-US') return '/app/en-US#pricing';
    return '/app/en#pricing';
  };

  const handleGoToPricing = () => {
    props.closeAccount(false);
    document.body.style.overflow = 'auto';
    window.location.assign(getLocalizedPricingPath());
  };

  const handleProjectLoad = () => {
    if (isProjectLoaded()) {
        // Signal a true project switch to App
        props.setProjectChangeKey((prevKey) => prevKey + 1);
    } else {
    }
};



async function handleOpen(id) {
  props.onShowLoader();
  props.setProjectId(id);

  // ✅ Reset AI states when opening a project (clear previous project's AI data)
  if (props.resetAIStates) {
    props.resetAIStates();
  }

  // Check if a project is currently loaded on the canvas
  const isProjectLoaded = props.canvas && (props.canvas.getObjects().length > 0 || props.canvas.backgroundImage);

  if (isProjectLoaded) {
      props.clearCanvas();
  } else {

  }

  try {
      const docRef = doc(db, "Projects", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
          console.error("No project found with ID:", id);
          props.onHideLoader();
          return;
      }

      const projectData = docSnap.data();
      const persistedProjectName = String(projectData.projectName || "").trim() || `project_${id}`;

      // Parse projectJson if it's a string
      let projectJson = projectData.projectJson;
      if (typeof projectJson === 'string') {
        try {
          projectJson = JSON.parse(projectJson);
        } catch (e) {
          console.error("❌ Error parsing projectJson in handleOpen:", e);
        }
      }

      if (projectJson && projectJson.backgroundImage) {
          setImageURL(projectJson.backgroundImage.src);
      }

      const fallbackBackgroundSrc =
        projectData?.imageUrls?.originalSource ||
        projectData?.imageUrls?.originalTreated ||
        projectData?.imageURL ||
        '';

      await props.loadProject(projectJson || projectData.projectJson, projectData.scaleFactor, fallbackBackgroundSrc);

      if (projectData.scaleFactor !== undefined) {
          props.setScaleFactor(projectData.scaleFactor);
      }

      if (isMountedRef.current) {
          setCanvasJson(projectJson || projectData.projectJson);

          if (projectData.canvasProjectImage && projectData.canvasProjectImage !== "MISSING_IMAGE_URL") {
              setImageURL(projectData.canvasProjectImage);
          }

          await props.closeAccount(false);
          await props.handleProjectName(persistedProjectName);

          // Signal project change after successful load
          props.setProjectChangeKey((prevKey) => prevKey + 1);
      }
  } catch (error) {
      console.error("An error occurred while opening the project:", error);
  } finally {
      props.onHideLoader();
      document.body.style.overflow = "auto";

      // ✅ Removed automatic Designer opening for existing projects
      // Designer will only open automatically for new projects

  }
}








  async function handleDelete(id) {
    if (isDeleteLocked) {
      toast.info(t('account.deleteUpgradePrompt', {
        defaultValue: 'Upgrade to Standard or Pro to unlock project deletion.'
      }));
      handleGoToPricing();
      return;
    }

    if (window.confirm(t("account.confirmDelete"))) {
      try {
        // Fetch the project from Firestore.
        const docRef = doc(db, "Projects", id);
        const docData = await getDoc(docRef);

        if (!docData.exists()) {
          console.warn(`No project found with ID: ${id}`);
          // Proceed with deleting the associated files even if the project document doesn't exist
        } else {
          let imageName = docData.data().imageName;
          if (!imageName) {
            console.error("Project name not found");
            return; // Exit if the project name is not found, no need to proceed.
          }


          const baseName = `${imageName}`;
          const baseNameUnderscore = baseName.replaceAll(" ", "_");

          // Construct paths for the images in various folders.
          const originalImagePath = `usersProjectsImages/${loggedInUserId}/canvasImages/${baseNameUnderscore}`;
          const canvasTreatedImagePath = `usersProjectsImages/${loggedInUserId}/canvasImages/canvasImagesTreated/${baseNameUnderscore}.png`;
          const canvasResizedImagePath = `usersProjectsImages/${loggedInUserId}/canvasImages/canvasImagesResized/${baseNameUnderscore}_resized.png`;
          const canvasWebPImagePath = `usersProjectsImages/${loggedInUserId}/canvasImages/canvasImagesWebP/${baseNameUnderscore}_webp.webp`;

          // Delete files from Firebase Storage
          const imagePaths = [
            originalImagePath,
            canvasTreatedImagePath,
            canvasResizedImagePath,
            canvasWebPImagePath,
          ];

          for (const path of imagePaths) {
            try {
              const imageRef = ref(storage, path);
              await deleteObject(imageRef);
            } catch (error) {
              if (error.code === 'storage/object-not-found') {
                // Some derived files are optional for newer projects.
                continue;
              } else {
                console.error(`Failed to delete ${path}:`, error);
              }
            }
          }
        }

        // Delete the project document from Firestore only if it exists.
        if (docData.exists()) {
          await deleteDoc(docRef);
        }

        props.setEditComplete((prev) => !prev);
        props.closeAccount(false);

        // Check if the project to be deleted is the current one, if so, clear the canvas.
        const currentProject = props.projects.find((project) => project.id === id);
        if (currentProject) {
          props.clearCanvas();
          props.setProjectName("");
        }
      } catch (error) {
        console.error("Error deleting project:", error);
      }
    }
  }





  async function handleEdit(id) {

    props.onShowLoader();

    const jsonProjectFile = props.projects.find((js) => js.id === id);

    const imageFile = props.projects.find((im) => im.id === id);

    const name = props.projects.find((na) => na.id === id);

    const projectName = prompt(t("account.enterNewName"), name.projectName);

    // Check if the user canceled the prompt
    if (projectName === null) {
      props.onHideLoader();
      return;
    }

    const docRef = doc(db, "Projects", id);
    const payload = {
      projectName: projectName.replaceAll(" ", "_") || name.projectName,
      userId: loggedInUserId,
      imageURL: jsonProjectFile.imageURL,
      projectJson: jsonProjectFile.projectJson,
    };
    await updateDoc(docRef, payload);
    props.handleProjectName(projectName);
    props.setEditComplete((prev) => !prev)

    props.onHideLoader();
  }


  const getResizedImageURL = (project) => {
    if (project) {
      // Parse projectJson if it's a string
      let projectJson = project.projectJson;
      if (typeof projectJson === 'string') {
        try {
          projectJson = JSON.parse(projectJson);
        } catch (e) {
          console.error("❌ Error parsing projectJson:", e);
          projectJson = null;
        }
      }

      if (project.imageUrls && project.imageUrls.resized) {
        // Use the new resized URL if available
        return project.imageUrls.resized;
      } else if (project.imageURL && project.imageURL !== "MISSING_IMAGE_URL") {
        if (String(project.imageURL).startsWith('data:image/')) {
          return project.imageURL;
        }
        // If imageUrls.resized is missing, construct the resized URL from imageURL
        const resizedURL = project.imageURL.replace(/\.png$/, "_resized.png");
        if (resizedURL === project.imageURL) {
          return project.imageURL;
        }
        const finalURL = resizedURL.replace("/canvasImages/", "/canvasImages/canvasImagesResized/");
        return finalURL;
      } else if (projectJson && projectJson.canvasProjectImage && projectJson.canvasProjectImage !== "MISSING_IMAGE_URL") {
        if (String(projectJson.canvasProjectImage).startsWith('data:image/')) {
          return projectJson.canvasProjectImage;
        }
        // Fallback to constructing resized URL from canvasProjectImage if available
        const resizedURL = projectJson.canvasProjectImage.replace(/\.png$/, "_resized.png");
        if (resizedURL === projectJson.canvasProjectImage) {
          return projectJson.canvasProjectImage;
        }
        const finalURL = resizedURL.replace("/canvasImages/", "/canvasImages/canvasImagesResized/");
        return finalURL;
      } else {
        console.error("❌ Image URLs are missing or invalid for project:", project.projectName);
        console.error("   Project data:", {
          id: project.id,
          projectName: project.projectName,
          hasImageUrls: !!project.imageUrls,
          hasImageURL: !!project.imageURL,
          hasProjectJson: !!project.projectJson
        });
        return "";
      }
    } else {
      return project?.imageURL || ""; // Fallback to original imageURL if necessary
    }
  };

  const getProjectImageFallbackURL = (project) => {
    if (!project) return "";

    let projectJson = project.projectJson;
    if (typeof projectJson === "string") {
      try {
        projectJson = JSON.parse(projectJson);
      } catch (e) {
        projectJson = null;
      }
    }

    if (project.imageUrls?.originalTreated) {
      return project.imageUrls.originalTreated;
    }

    if (project.imageURL && project.imageURL !== "MISSING_IMAGE_URL") {
      return project.imageURL;
    }

    if (projectJson?.canvasProjectImage && projectJson.canvasProjectImage !== "MISSING_IMAGE_URL") {
      return projectJson.canvasProjectImage;
    }

    if (project.imageUrls?.originalSource) {
      return project.imageUrls.originalSource;
    }

    return "";
  };







  useEffect(() => {
    if (!props.setIsProjectList) projectNameRef.current.focus();
  });

  useEffect(() => {
    return () => {
      isMountedRef.current = false; // Component will unmount
      props.onHideLoader();
      // document.body.style.overflow = "auto";
    };
  }, []); // Empty dependency array ensures this runs on mount and unmount only

  // Real-time listener for user data
  useEffect(() => {
    if (!currentUser || currentUser.email.startsWith("guest_")) {
      setUserData(null);
      return;
    }

    const userDocRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        setUserData(doc.data());
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.uid || currentUser.email?.startsWith("guest_")) {
      setLocalProjects([]);
      return undefined;
    }

    const projectsQuery = query(
      collection(db, "Projects"),
      where("userId", "==", currentUser.uid)
    );

    const unsubscribe = onSnapshot(projectsQuery, (snapshot) => {
      const nextProjects = snapshot.docs.map((docSnapshot) => ({
        ...docSnapshot.data(),
        id: docSnapshot.id,
      }));

      setLocalProjects(nextProjects);
    }, (error) => {
      console.error("Error subscribing to account project list:", error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleOpenProject = async (id) => {
    await handleOpen(id);
  };


  return (
    <Modal
      show={true}
      onHide={() => {
        props.closeAccount(false);
        document.body.style.overflow = "auto";
      }}
      backdrop="static"
      backdropClassName="account-modal-backdrop"
      keyboard={false}
      className="account-modal-root"
      dialogClassName="custom-modal"


    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t("account.account")}
          {featureFlags.PROJECT_LIMITS_ENABLED && !limitsLoading && currentUser && !currentUser.email.startsWith("guest_") && (
            <small className="text-muted ms-2">
              ({projectCount}/{projectLimit} {t("account.projectsLabel")})
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
      <Form>
          <Form.Group controlId="searchProjects">
            <Form.Control
              type="text"
              placeholder={t("account.searchProjects")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>
        </Form>

        {isDownloading && <div className="loader">{t("account.loading")}</div>}

        {!props.isProjectList && props.projectName && (
          <div className="enterName">
            <Form>
              <Form.Group controlId="exampleForm.ControlInput1">
                {!props.projectName && (
                  <Form.Label>{t("account.enterName")}</Form.Label>
                )}
                {props.projectName && (
                  <Form.Control
                    type="text"
                    value={props.projectName.replaceAll("_", " ")}
                    ref={projectNameRef}
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                )}
              </Form.Group>
            </Form>

          </div>
        )}



        <div className="currentProject">

        <div style={{ position: 'relative' }}>
          <h2>{t("account.selectProject")}</h2>
          {/* Project Limit Information */}
          {featureFlags.PROJECT_LIMITS_ENABLED && !limitsLoading && currentUser && !currentUser.email.startsWith("guest_") && (
            <div className="project-limit-info mt-2">
              {(isAtLimit || hasNoProjectSlots) && (
                <div className="alert alert-danger alert-sm">
                  <strong>{t("account.projectLimitReachedTitle")}</strong> {t("account.projectLimitReachedMessage", { current: projectCount, limit: projectLimit })}
                  <br />{t("account.projectLimitReachedCta")}
                  <div className="mt-2">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleGoToPricing}
                    >
                      {t("account.projectLimitUpgradeButton", { defaultValue: "View pricing plans" })}
                    </Button>
                  </div>
                </div>
              )}
              {!isAtLimit && !hasNoProjectSlots && projectCount >= projectLimit * 0.8 && (
                <div className="alert alert-warning alert-sm">
                  <strong>{t("account.projectLimitApproachingTitle")}</strong> {t("account.projectLimitApproachingMessage", { current: projectCount, limit: projectLimit })}
                </div>
              )}
              {!hasNoProjectSlots && projectCount < projectLimit * 0.8 && (
                <div className="text-muted small">
                  {t("account.projectLimitRemaining", { remaining: projectLimit - projectCount })}
                </div>
              )}
            </div>
          )}
        </div>

          <h3>{t("account.savedProjects")}</h3>
          <hr />
          <div className="projectDetails">
            {filteredProjects.length === 0 && (
              <div className="alert alert-secondary">
                {t("account.noProjects", { defaultValue: "No projects available yet." })}
              </div>
            )}
            {filteredProjects.map((project) => (
              <Fragment key={project.id}>
                <ul>
                  <li>
                    <div className="projectTitle">
                      <h4>{getProjectDisplayName(project)}</h4>
                      <div className="projectTimestamp">
                        {(() => {
                          const timestamp = project.lastSaved || project.savedTimestamp || project.createdAt || project.timestamp;
                          if (timestamp) {
                            const date = new Date(timestamp);
                            const now = new Date();
                            const diffTime = Math.abs(now - date);
                            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                            const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
                            const diffMinutes = Math.floor(diffTime / (1000 * 60));

                            if (diffDays > 0) {
                              const dayUnit = diffDays > 1 ? t("account.timeAgo.days") : t("account.timeAgo.day");
                              return t("account.timeAgo.formatDay", { count: diffDays, unit: dayUnit });
                            } else if (diffHours > 0) {
                              const hourUnit = diffHours > 1 ? t("account.timeAgo.hours") : t("account.timeAgo.hour");
                              return t("account.timeAgo.formatHour", { count: diffHours, unit: hourUnit });
                            } else if (diffMinutes > 0) {
                              const minuteUnit = diffMinutes > 1 ? t("account.timeAgo.minutes") : t("account.timeAgo.minute");
                              return t("account.timeAgo.formatMinute", { count: diffMinutes, unit: minuteUnit });
                            } else {
                              return t("account.timeAgo.justNow");
                            }
                          }
                          return '';
                        })()}
                      </div>
                    </div>
                    <div className="projectImageContainer">
                      <img
                        className="projectImage"
                        src={getResizedImageURL(project)}
                        ref={projectImageRef}
                        onClick={() => handleOpenProject(project.id)}
                        alt={getProjectDisplayName(project)}
                        onError={(e) => {
                          const fallbackUrl = getProjectImageFallbackURL(project);

                          if (fallbackUrl && e.currentTarget.src !== fallbackUrl) {
                            e.currentTarget.src = fallbackUrl;
                            return;
                          }

                          e.currentTarget.style.border = '2px solid red';
                        }}
                      />
                    </div>
                    <div className="listButttons">
                      <Button
                        variant="secondary"
                        className="openProject"
                        onClick={() => handleOpenProject(project.id)}
                      >
                        <OpenInFull fontSize="small" style={{ marginRight: "5px" }} />
                        {t("account.openProject")}
                      </Button>





                        <div className="listButtonCell">
                          <Tooltip
                            title={tooltipMessage}
                            position="top"
                            trigger="mouseenter"
                            disabled={!isGuestUser}
                          >
                            <Button
                              variant="secondary"
                              className="editName"
                              onClick={() => handleEdit(project.id)}
                              disabled={isGuestUser}
                            >
                              <Edit fontSize="small" style={{ marginRight: "5px" }} />
                              {t("account.editName")}
                            </Button>
                          </Tooltip>
                        </div>

                        <div className="listButtonCell">
                          <Tooltip
                            title={tooltipMessage}
                            position="top"
                            trigger="mouseenter"
                            disabled={!isGuestUser}
                          >
                            <Button
                              variant="secondary"
                              className="editName"
                              onClick={() => props.downloadpng(project.imageURL, project.projectName)}
                              disabled={isGuestUser}
                            >
                              <CloudDownload fontSize="small" style={{ marginRight: "5px" }} />
                              {t("account.downloadPng")}
                            </Button>
                          </Tooltip>
                        </div>

                        <div className="listButtonCell listButtonCellFull">
                          <Tooltip
                            title={isGuestUser ? tooltipMessage : (isDeleteLocked ? trialDeleteLockMessage : '')}
                            position="top"
                            trigger="mouseenter"
                            disabled={!isGuestUser && !isDeleteLocked}
                          >
                            <Button
                              variant="danger"
                              className="deleteProject"
                              onClick={() => handleDelete(project.id)}
                              disabled={isGuestUser}
                            >
                              <Delete fontSize="small" style={{ marginRight: "5px" }} />
                              {t("account.deleteProject")}
                            </Button>
                          </Tooltip>
                        </div>

                      <div className="projectDivider"></div>
                    </div>

                  </li>
                </ul>
              </Fragment>
            ))}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default Account;
